/**
 * Content sanitizer for user-authored messages (SMS, email, WhatsApp).
 * Mirrors the backend PHP content_sanitizer.php logic.
 *
 * The invitation message is a short personal greeting only — the
 * invitation URL is injected server-side.
 */

const FORBIDDEN_PATTERNS = [
    // ── URLs ──
    /https?:\/\/[^\s]+/i,
    /ftp:\/\/[^\s]+/i,
    /\/\/[a-z0-9-]+\.[a-z]{2,}/i,
    /www\.[a-z0-9-]+\.[a-z]{2,}[^\s]*/i,
    /\b[a-z0-9-]+\.(com|net|org|ro|io|co|eu|info|biz|me|xyz|dev|app|site|online|store|shop|link|click|top|win|fun|icu|buzz|tech|live|pro|cloud|space|website|club)(\/[^\s]*)?\b/i,
    /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(:\d+)?(\/[^\s]*)?\b/,
    /hxxps?:\/\/[^\s]+/i,
    /[a-z0-9-]+\s*[[(]\s*dot\s*[\])]\s*[a-z]{2,}/i,
    /\b(bit\.ly|tinyurl\.com|t\.co|goo\.gl|is\.gd|v\.gd|buff\.ly|ow\.ly|rebrand\.ly|shorturl\.at|cutt\.ly|tiny\.cc)[^\s]*/i,

    // ── Email addresses ──
    /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i,

    // ── Phone numbers & number sequences ──
    // Romanian phone formats
    /\b\+?4?0?7\d{8}\b/,
    /\b\+?4?0?[23]\d{8}\b/,
    // International format with + prefix
    /\+\d[\d\s-]{7,}/,
    // Any sequence of 5+ consecutive digits
    /\d{5,}/,
    // Digits separated by spaces/dashes that together form 5+ digits
    /\d[\d\s\-.]{4,}\d/,
];

const ALLOWED_DOMAIN = /^(https?:\/\/)?(www\.)?invitatiismart\.ro/i;

function isYear(str) {
    const trimmed = str.trim();
    if (!/^\d{4}$/.test(trimmed)) return false;
    const n = parseInt(trimmed, 10);
    return n >= 1900 && n <= 2099;
}

/**
 * Detect forbidden content in text. Returns { clean: boolean, matches: string[] }
 */
export function detectForbiddenContent(text) {
    if (!text) return { clean: true, matches: [] };

    const matches = [];
    for (const pattern of FORBIDDEN_PATTERNS) {
        const re = new RegExp(pattern.source, pattern.flags + (pattern.flags.includes('g') ? '' : 'g'));
        let m;
        while ((m = re.exec(text)) !== null) {
            const val = m[0].trim();
            // Allow invitatiismart.ro URLs
            if (ALLOWED_DOMAIN.test(val)) continue;
            // Allow standalone years (common in greeting messages like "nunta 2026")
            if (isYear(val)) continue;
            matches.push(m[0]);
        }
    }

    return {
        clean: matches.length === 0,
        matches: [...new Set(matches)],
    };
}

/**
 * Validate message content. Returns { valid: boolean, error: string|null }
 */
export function validateMessageContent(text) {
    const result = detectForbiddenContent(text);
    if (!result.clean) {
        return {
            valid: false,
            error: 'Mesajul nu poate contine link-uri, adrese email sau numere de telefon. Link-ul catre invitatia ta va fi adaugat automat.',
        };
    }
    return { valid: true, error: null };
}

// Backward-compatible alias
export const detectUrls = detectForbiddenContent;
